import { Dispatch, SetStateAction } from 'react';
import { SWRConfiguration, SWRResponse } from 'swr';
import { CustomFieldType } from '../components/CustomField/CustomField.type';
import { AddMembersToGroupOptionsMap } from '../components/GroupModal/AddMemberForm';
import { UserGroupsFormOptionsMap } from '../components/UsersPage/UserGroupInput/UserGroupModal/AddGroupForm';
import { defaultCallRecordsVisibilityOptionsMap } from '../containers/Pages/AdminPage/ChannelViews/CallView/CallViewContainer';

// get given typeof keys from an object
export type KeysMatching<T, V> = { [K in keyof T]: T[K] extends V ? K : never }[keyof T];

export type convertToNever<T> = { [K in keyof T]?: never };

export interface Sort {
    orderBy: any[];
    sorted: boolean;
}
export interface Pageable {
    number: number;
    offset: number;
    size: number;
    sort: Sort;
    sorted: boolean;
    unpaged: boolean;
}

export type PaginatedData<T> = {
    content: T[];
    empty: boolean;
    numberOfElements: number;
    offset: number;
    pageNumber: number;
    pageable: Pageable;
    size: number;
    totalPages: number;
    totalSize: number;
};

/////////////////////////////////////////
export interface RefreshTokenPostData {
    refreshToken: string;
    tenantId: string;
}

/////////////////////////////////////////

export const tabTypeMap = {
    DEAL: 'DEAL',
    ORGANIZATION: 'ORGANIZATION',
    PLUGIN: 'PLUGIN',
    TICKET: 'TICKET',
    USER: 'USER'
} as const;

export type TabType = keyof typeof tabTypeMap;
interface BasicTab {
    key: string;
    type: TabType;
    url: string;
}
export interface DealTab extends BasicTab {
    name: string;
    type: typeof tabTypeMap.DEAL;
}
export interface OrganizationTab extends BasicTab {
    name: string;
    type: typeof tabTypeMap.ORGANIZATION;
}
export interface PluginTab extends BasicTab {
    pluginId: string;
    type: typeof tabTypeMap.PLUGIN;
}
export interface UserTab extends BasicTab {
    email: string;
    fullName: string;
    type: typeof tabTypeMap.USER;
    phone: string;
}
export interface SimpleTicketTab extends BasicTab {
    channel:
        | typeof channelTypeMap.CALL
        | typeof channelTypeMap.EMAIL
        | typeof channelTypeMap.INTEGRATION
        | typeof channelTypeMap.OTHER
        | typeof channelTypeMap.WEB;
    description: string;
    requester: string;
    status: Ticket['status'];
    subject: string;
    type: typeof tabTypeMap.TICKET;
    isSidebarPluginRendered: boolean;
}
export interface ChatTicketTab extends Omit<SimpleTicketTab, 'channel'> {
    channel: typeof channelTypeMap.CHAT;
    chatId: string;
    chatSessionId: string;
    unreadMessagesCount: number;
}
export type TicketTab = SimpleTicketTab | ChatTicketTab;
export type Tab = DealTab | OrganizationTab | PluginTab | TicketTab | UserTab;
export const priorityOptionsMap = {
    LOW: 'LOW',
    NORMAL: 'NORMAL',
    HIGH: 'HIGH',
    URGENT: 'URGENT'
    // CLOSED: 'CLOSED';
} as const;

export type priority = keyof typeof priorityOptionsMap;

export const ticketTypeOptionsMap = {
    QUESTION: 'QUESTION',
    INCIDENT: 'INCIDENT',
    PROBLEM: 'PROBLEM',
    TASK: 'TASK'
} as const;

export type ticketType = keyof typeof ticketTypeOptionsMap;

export const statusi18nKeyMap = {
    New: 'ticketStatus.New',
    Open: 'ticketStatus.Open',
    Pending: 'ticketStatus.Pending',
    'On Hold': 'ticketStatus.OnHold',
    Solved: 'ticketStatus.Solved',
    Closed: 'ticketStatus.Closed'
} as const;
export const statusOptions = {
    New: 'New',
    Open: 'Open',
    Pending: 'Pending',
    'On Hold': 'OnHold',
    Solved: 'Solved',
    Closed: 'Closed'
} as const;

export const statusMap = {
    '1': 'New',
    '2': 'Open',
    '3': 'Pending',
    '6': 'On Hold',
    '4': 'Solved',
    '5': 'Closed'
} as const;

export const reverseStatusMap = {
    New: '1',
    Open: '2',
    Pending: '3',
    'On Hold': '6',
    Solved: '4',
    Closed: '5'
} as const;

export type status = keyof typeof statusOptions;

export const commentTypeOptionsMap = { public: 'public', internal: 'internal' } as const;
export const commentTypeOptions = { public: 'Public', internal: 'Internal' } as const;
export type commentType = keyof typeof commentTypeOptions;

export const commentListViewOptions = { all: 'All', ...commentTypeOptions } as const;
export type commentListViewType = keyof typeof commentListViewOptions;

export const ticketInteractionViewOptionsMap = {
    conversations: 'conversations',
    chat: 'chat',
    gsocialChat: 'gsocialChat',
    events: 'events'
} as const;

export type ticketInteractionViewViewType = keyof typeof ticketInteractionViewOptionsMap;

// export type ticketType = 'Question' | 'Incident' | 'Problem' | 'Task';

export const tagBackgroundColors = {
    New: '#fa8c16',
    Open: '#fa541c',
    Pending: '#722ed1',
    'On Hold': '#000',
    Solved: '#389e0d',
    Closed: '#8c8c8c'
} as const;

export const tagBackgroundHoveredColors = {
    New: '#fb9d39',
    Open: '#FB6E3E',
    Pending: '#874DD8',
    'On Hold': '#4c4c4c',
    Solved: '#56AD31',
    Closed: '#9D9D9D'
} as const;

// export interface Organization {
//     id: string;
//     name: string;
//     tags: string[];
// }

/////////////////////////////////////////

export enum statisticOptions {
    unsolvedTickets = 'unsolvedTickets',
    ticketStatistics = 'ticketStatistics'
}

export type statisticOptionsTypes = keyof typeof statisticOptions;

export enum unsolvedTicketsObjectType {
    unsolvedUserTickets = 'unsolvedUserTickets',
    unsolvedGroupTickets = 'unsolvedGroupTickets'
}
export type unsolvedTicketsObjectKeys = keyof typeof unsolvedTicketsObjectType;
export type unsolvedTicketsType = { [K in unsolvedTicketsObjectKeys]: number };

export enum ticketStatisticsObjectType {
    openedLastWeek = 'openedLastWeek',
    solvedLastWeek = 'solvedLastWeek'
}
export type ticketStatisticsObjectKeys = keyof typeof ticketStatisticsObjectType;
export type ticketStatisticsType = { [K in unsolvedTicketsObjectKeys]: number };

export const statisticObjectType = { ...unsolvedTicketsObjectType, ...ticketStatisticsObjectType };

export type DashboardStatisticsType = {
    [K in statisticOptionsTypes]: K extends 'unsolvedTickets'
        ? unsolvedTicketsType
        : K extends 'ticketStatistics'
          ? ticketStatisticsType
          : never;
};

export interface FilterOverview {
    [filterKey: string]: { name: string; count: number; order: number };
}

// export interface Filter {
//     allConditions: FilterCondition[];
//     anyConditions: FilterCondition[];
//     authorizedGroupIds: string[];
//     authorizedUserIds: string[];
//     columns: FilterColumn[];
//     description: string;
//     enabled: boolean;
//     id: number;
//     name: string;
//     order: number;
//     orderByFieldKey: string;
//     ownerId: number;
//     predefined: boolean;
//     public: boolean;
// }

// export interface FilterColumn {
//     key: string;
//     name: string;
// }

// export interface FilterCondition {
//     fieldKey: string;
//     operator: string;
//     value: string;
// }

/////////////////////////////////////////

export enum userRolesMap {
    ROLE_ADMIN = 'ROLE_ADMIN',
    ROLE_AGENT = 'ROLE_AGENT',
    ROLE_END_USER = 'ROLE_END_USER'
}

export enum extendedUserRolesMap {
    ROLE_ADMIN = 'ROLE_ADMIN',
    ROLE_AGENT = 'ROLE_AGENT',
    ROLE_END_USER = 'ROLE_END_USER',
    ROLE_INTEGRATION = 'ROLE_INTEGRATION'
}

export const userRoles = {
    ROLE_ADMIN: 'Admin',
    ROLE_AGENT: 'Agent',
    ROLE_END_USER: 'End-User'
} as const;
export const extendedUserRoles = {
    ...userRoles,
    ROLE_INTEGRATION: 'Integration'
} as const;

export type roles = keyof typeof extendedUserRoles;

export interface Organization {
    id: number;
    name: string;
    details?: string;
    notes?: string;
    group?: Group;
    groups?: Group[];
    tags?: string[];
    fields?: Field[];
    userCount?: number;
}
export type SimpleOrganization = Omit<Organization, 'users' | 'fields' | 'tags' | 'groups' | 'group'> & {
    fieldMap?: Record<string, Field>;
    group?: number;
};

export interface IEmail {
    email: string;
    verified: boolean;
}

export interface IPhone {
    phone: string;
    verified: boolean;
}
export type DetailedUser = {
    email: string;
    emails: IEmail[];
    enabled: boolean;
    fields: Field[];
    fieldMap?: Record<string, Field>;
    firstName: string;
    fullName: string;
    groups: Group[];
    id: number;
    language: languagesType;
    lastName: string;
    organization: Organization & { users: string[] };
    phone: string;
    phones: IPhone[];
    role: { authority: roles; impliedAuthorities: roles[] };
    serializedValue?: string;
    tags: string[];
    profilePicture?: string;
    userFriendlyValue?: string; // TODO: cagri => check type
};

export type SimpleUser = Omit<DetailedUser, 'roles' | 'groups'>;
export type SimpleUserForElastic = Omit<SimpleUser, 'role'> & { role: roles };
export type AutoCompleteUserSearch = Pick<
    SimpleUser,
    'email' | 'fullName' | 'id' | 'phone' | 'serializedValue' | 'userFriendlyValue'
>;

export interface Group {
    id: number;
    name: string;
}
export interface GroupWithUsers extends Group {
    users: SimpleUser[];
}

export const UserFormDataMap = {
    email: 'email',
    phone: 'phone',
    groups: 'groups',
    fullName: 'fullName',
    role: 'roles',
    password: 'password'
} as const;
export type UserFormData = {
    email: string;
    phone: string;
    groups?: string[];
    fullName?: string;
    role?: roles;
    password?: string;
};

export type UserCreatePostData = Omit<UserFormData, 'groups'>;
export type UserEditPostData = {
    email?: string;
    emails?: string[];
    groups?: Group[];
    organization?: number;
    phone?: string;
    phones?: string[];
    role: roles; // required for request validation
    roleToChange?: roles;
    userId: number; // required for request
};
export type UserRoleUpdatePostData = Pick<RequesterEditPostData, 'role' | 'userId'>;
export type RequesterEditPostData = {
    [USER_FORM_FIELDS_KEY]: fieldPostData[];
    email?: string;
    emails?: string[];
    fullName?: string;
    groups?: Group[];
    organization?: number;
    phone?: string;
    phones?: string[];
    role: roles;
    roleToChange?: roles;
    userId: number;
};

export type UserDeleteData = {
    id: number;
    role: roles;
};

export type GroupFormData = { name: string };

export type GroupEditPostData = {
    groupId: number;
    name?: string;
};
export type GroupMembersFormData = { users: string; option: AddMembersToGroupOptionsMap };
export type UserGroupsFormData = { groupId: number; option: UserGroupsFormOptionsMap };

export type GroupCreatePostData = { name: string };

export type Assignee = {
    group: Group;
    user: SimpleUser;
};
export type AssigneePostable = {
    group: Group;
    user?: SimpleUser;
};

export interface Attachment {
    bucket: string;
    commentId: number;
    filename: string;
    id: number;
    objectKey: string;
    objectThumbKey: string;
    objectThumbUrl: string;
    objectUrl: string;
    mimeType: string;
    size: number;
    inline: boolean;
}
/////////////////////////////////////////
export const Permission = {
    AGENT_ONLY: 'AGENT_ONLY',
    EDITABLE_BY_END_USERS: 'EDITABLE_BY_END_USERS',
    READONLY_FOR_END_USERS: 'READONLY_FOR_END_USERS',
    READONLY: 'READONLY',
    END_USER_ONLY: 'END_USER_ONLY'
};
export type permissionType = keyof typeof Permission;

export type FieldDefinitionMap = Record<string, IFieldDefinition>;
export enum FieldScopeTypeMap {
    TICKET = 'TICKET',
    ORGANIZATION = 'ORGANIZATION',
    USER = 'USER',
    DEAL = 'DEAL'
}
export type FieldScopeType = keyof typeof FieldScopeTypeMap;
export interface IFieldDefinition {
    attributes?: attributesType[];
    descriptionForAgents: string;
    descriptionForEndUsers?: string;
    enabled: boolean;
    id?: number;
    key: string;
    name: string;
    options?: CustomFieldOption[];
    permission: permissionType;
    required: FieldRequirement;
    systemField: boolean;
    gsocialField: boolean;
    titleForAgents: string;
    titleForEndUsers?: string;
    type: CustomFieldType;
    scope: FieldScopeType;
    defaultField: boolean; // *d. prefix => behavior like system field but originally custom field
}

export enum IFieldDefinitionMap {
    attributes = 'attributes',
    descriptionForAgents = 'descriptionForAgents',
    descriptionForEndUsers = 'descriptionForEndUsers',
    id = 'id',
    key = 'key',
    name = 'name',
    options = 'options',
    permission = 'permission',
    required = 'required',
    systemField = 'systemField',
    titleForAgents = 'titleForAgents',
    titleForEndUsers = 'titleForEndUsers',
    type = 'type'
}

export enum Attributes {
    ALLOW_NEW_VALUES = 'ALLOW_NEW_VALUES',
    FIXED_PLACE_IN_UI = 'FIXED_PLACE_IN_UI',
    FIXED_REQUIREMENT = 'FIXED_REQUIREMENT'
}

export type attributesType = keyof typeof Attributes;
export interface Field {
    id: number;
    key: string;
    serializedValue: string;
    type: CustomFieldType;
    userFriendlyValue: string;
    value: string | number | DetailedUser | Assignee | Ticket['status'] | { id: number };
}

export const fomrPermissionsOptions = {
    AGENT_ONLY: { value: 'AGENT_ONLY', label: 'Editable for agents' },
    // END_USER_ONLY: {
    //     value: 'END_USER_ONLY',
    //     label: 'Editable for Customers',
    //     // tooltup: 'Enable end users to select this form when submitting a ticket.',
    // },
    END_USER_AND_AGENT: {
        value: 'END_USER_AND_AGENT',
        label: 'Editable for Customers and agents'
    }
} as const;

export enum FormPermission {
    AGENT_ONLY = 'AGENT_ONLY',
    // END_USER_ONLY = 'END_USER_ONLY',
    END_USER_AND_AGENT = 'END_USER_AND_AGENT'
}

export enum FieldRequirementTypeMap {
    TRUE = 'TRUE',
    FALSE = 'FALSE',
    'TARGET_STATUS' = 'TARGET_STATUS'
}
export type FieldRequirement = { type: FieldRequirementTypeMap; ids: number[] };
// export type ConditionActionFieldRequirement = {
//     type: Omit<FieldRequirementTypeMap, 'TARGET_STATUS'>;
//     ids: number[];
// };
export interface FieldConditionAction {
    fieldKey: string;
    required: FieldRequirement;
}
export interface FieldCondition {
    action: FieldConditionAction;
    value: string[];
}
export interface FieldRuleOnForm {
    conditions: FieldCondition[];
    key: string;
    required: FieldRequirement;
}
export enum TicketFormScopeTypeMap {
    TICKET = 'TICKET',
    DEAL = 'DEAL'
}
export type TicketFormScopeType = keyof typeof TicketFormScopeTypeMap;
export interface IForm {
    consent: string;
    description: string;
    enabled: boolean;
    fields: FieldRuleOnForm[];
    id?: number;
    name: string;
    permission: keyof typeof FormPermission;
    scope: TicketFormScopeType;
}

export const TicketRelationTypeMap = { FOLLOW_UP: 'FOLLOW_UP', MERGE: 'MERGE' };
export type TicketRelationType = keyof typeof TicketRelationTypeMap;
export type TicketRelation = {
    sourceTicket: string;
    targetTicket: string;
    type: TicketRelationType;
};

export interface NewTicket {
    callMergeStatus: 'TO_BE_DECIDED' | 'MERGED' | 'NOT_MERGED';
    channel: channelType;
    comments: Comment[];
    createdAt: number;
    fieldMap: Record<string, Field>;
    form: IForm;
    key: string;
    resolution: 'MERGED_BY_CALL' | 'MERGED_BY_WEB' | 'CLOSED';
    relation: TicketRelation[];
    solvedAt: number;
    updatedAt: number;
}

/////////////////////////////////////////
export const channelGSocialTypeMap = {
    WHATSAPP: 'WHATSAPP',
    WHATSAPP_GUPSHUP: 'WHATSAPP_GUPSHUP',
    WHATSAPP_CONNEXEASE: 'WHATSAPP_CONNEXEASE',
    GOOGLE_REVIEWS: 'GOOGLE_REVIEWS',
    TWITTER: 'TWITTER',
    /**
     * We split the INSTAGRAM channel into 2 different channels (INSTAGRAM_DM, INSTAGRAM_COMMENT)
     * But we still need to keep the INSTAGRAM channel for the old tickets
     */
    INSTAGRAM: 'INSTAGRAM',
    INSTAGRAM_COMMENT: 'INSTAGRAM_COMMENT',
    INSTAGRAM_DM: 'INSTAGRAM_DM',
    FACEBOOK: 'FACEBOOK',
    SIKAYET_VAR: 'SIKAYET_VAR',
    GOOGLE_PLAY_STORE: 'GOOGLE_PLAY_STORE',
    APPLE_APP_STORE: 'APPLE_APP_STORE',
    TECMONY_CHAT: 'TECMONY_CHAT'
} as const;
export type channelGSocialType = keyof typeof channelGSocialTypeMap;

export const channelGSocialChatTypeMap = {
    [channelGSocialTypeMap.WHATSAPP]: channelGSocialTypeMap.WHATSAPP,
    [channelGSocialTypeMap.WHATSAPP_GUPSHUP]: channelGSocialTypeMap.WHATSAPP_GUPSHUP,
    [channelGSocialTypeMap.TECMONY_CHAT]: channelGSocialTypeMap.TECMONY_CHAT,
    [channelGSocialTypeMap.WHATSAPP_CONNEXEASE]: channelGSocialTypeMap.WHATSAPP_CONNEXEASE,
    [channelGSocialTypeMap.INSTAGRAM]: channelGSocialTypeMap.INSTAGRAM,
    [channelGSocialTypeMap.INSTAGRAM_DM]: channelGSocialTypeMap.INSTAGRAM_DM,
    [channelGSocialTypeMap.INSTAGRAM_COMMENT]: channelGSocialTypeMap.INSTAGRAM_COMMENT,
    [channelGSocialTypeMap.TWITTER]: channelGSocialTypeMap.TWITTER
} as const;
export type channelGSocialChatType = keyof typeof channelGSocialChatTypeMap;

export const channelTypeMap = {
    CALL: 'CALL',
    CHAT: 'CHAT',
    EMAIL: 'EMAIL',
    INTEGRATION: 'INTEGRATION',
    IMPORT: 'IMPORT',
    SCHEDULER: 'SCHEDULER',
    TRIGGER: 'TRIGGER',
    WEB: 'WEB',
    OTHER: 'OTHER',
    USER_FORM: 'USER_FORM',
    AUTOMATION: 'AUTOMATION',
    API: 'API',
    ...channelGSocialTypeMap
} as const;
export type channelType = keyof typeof channelTypeMap;

export interface Ticket {
    id: number;
    key: string;
    subject: string;
    creator: DetailedUser;
    channel: channelType;
    description: Comment['body'];
    status: { id: number; name: status };
    comments: Comment[] | Comment[];
    createdAt: number;
    updatedAt: number;
    form: IForm;
    fields: Field[];
    // optionals
    changeGroups?: Event[];
    mergedTickets?: any[];
    resolution?: 'MERGED_BY_CALL' | 'MERGED_BY_WEB';
    callMergeStatus?: 'TO_BE_DECIDED' | 'MERGED' | 'NOT_MERGED';
    priority?: priority;
    type?: ticketType;
    attachments?: Attachment[];
    tags?: string[];
    requester?: DetailedUser;
    assignee?: AssigneePostable;
    followers?: SimpleUser[];
    emailCC?: SimpleUser[];
    solvedAt?: number;
}

export interface EditTicketReturnedData extends NewTicket {
    changeGroups: Event[];
    createdAt: number;
    creator: SimpleUser;
    description: Comment['body'];
    id: number;
    key: string;
    requester: SimpleUser;
    subject: string;
    status: status;
    updatedAt: number;
}

export interface fieldPostData {
    key: string;
    value: string | number;
}
export interface CreateTicketPostData {
    comment: ICommentCreateTicket;
    fields: fieldPostData[];
}

export interface EditTicketPostData {
    id: string;
    comment?: ICommentCreateTicket & { externalId?: number };
    fields?: fieldPostData[];
}
export interface CreateTicketReturnedData extends EditTicketReturnedData {
    creator: DetailedUser;
}

export interface CreateCallTicketReturnedData extends CreateTicketReturnedData {
    description: string;
    assignee: Pick<Assignee, 'group'> & { user: DetailedUser };
}

export const CallDirection = {
    INBOUND: 'INBOUND',
    OUTBOUND: 'OUTBOUND'
};
export type callDirectionType = keyof typeof CallDirection;
export interface CallContent {
    afterCallDescription: string;
    afterCallDuration: string;
    agentMail: string;
    callId: string;
    callDuration: unknown;
    callEndedAt: number;
    callEndStatus: 'MISSING' | string;
    comment: Comment;
    createdAt: number;
    destination: string;
    direction: callDirectionType;
    disposition: unknown;
    holdCount: unknown;
    id: number;
    localHangup: unknown;
    queue: unknown;
    recording: { id: number; fileName: string; objectKey: string; objectUrl: string; bucket: string; mimeType: string };
    satisfactionScore: unknown;
    source: string;
    totalHoldDuration: unknown;
    waitDuration: unknown;
}

export interface Comment {
    attachments: Attachment[];
    body: string;
    call: CallContent;
    channel: channelType;
    commentCCs: string[];
    createdAt: number;
    creator: Omit<DetailedUser, 'userFriendlyValue'>;
    externalId: number;
    id: number;
    mentionedUsers: string[];
    publicVisible: boolean;
    ticketKey: string;
    toEmail: string; // requester
    toId: number; // requester id
}

export type OrganizationFormData = Pick<Organization, 'name' | 'notes' | 'details'>;
export type CreateOrganizationPostData = OrganizationFormData;
export type CreateOrganizationReturnData = Organization;
export type UpdateOrganizationPostData = {
    [ORGANIZATION_FORM_FIELDS_KEY]: fieldPostData[];
    organizationId: number;
    groupId?: number;
} & Partial<CreateOrganizationPostData>;

export interface TicketFormData {
    subject?: string;
    comment?: string;
    status?: status;
    tags?: string[];
    assignee?: string[];
    requester?: string | number | Array<number | string>;
    followers?: { key: number; value: number; label: string }[];
    distributionListTo?: string;
    commentType?: commentType;
    emailCC?: string[];
    organizationName?: string;
    organizationNotes?: string;
    organizationDetails?: string;
    priority?: priority;
    type?: ticketType;
}

export type ICommentCreateTicket = Pick<Comment, 'body' | 'publicVisible'> & {
    attachmentIds?: number[];
    channel: channelType;
    creator: { key: 'us.id' | 'us.email' | 'us.phone'; value: string }[];
    mentionedUsers?: number[];
};

export const actionAfterSubmitText = {
    stay: 'Stay on Ticket',
    // next: 'Next Ticket',
    close: 'Close tab'
} as const;
export type actionAfterSubmit = keyof typeof actionAfterSubmitText;

/////////////////////////////////////////
export const allAdminSidebarOptionsMap = {
    overview: 'overview',
    marketplace: 'marketplace',
    people: 'people',
    userFields: 'user-fields',
    organizationFields: 'organization-fields',
    ticketFields: 'ticket-fields',
    ticketForms: 'ticket-forms',
    tags: 'tags',
    macros: 'macros',
    filters: 'filters',
    userFilters: 'user-filters',
    organizationFilters: 'organization-filters',
    companySettings: 'company-settings',
    securitySettings: 'security-settings',
    scheduleSettings: 'schedule-settings',
    automations: 'automations',
    triggers: 'triggers',
    serviceLevelAgreements: 'service-level-agreements',
    email: 'email',
    chat: 'chat',
    call: 'call',
    gSocial: 'g-social',
    import: 'import',
    subscription: 'subscription',
    quickStart: 'quick-start',
    advancedReports: 'advanced-reports',
    apps: 'apps',
    external: 'external',
    audit: 'audit'
} as const;

export const adminSidebarHomeOptions = {
    overview: { text: 'Overview', url: '/admin' },
    'quick-start': { text: 'quickStart', url: '/admin/quick-start' },
    marketplace: { text: 'Marketplace', url: '/admin/marketplace' },
    audit: { text: 'Audit', url: '/admin/audit' }
} as const;
export type adminSidebarHomeOptionsType = keyof typeof adminSidebarHomeOptions;

export const adminSidebarManageOptions = {
    filters: { text: 'Filters.title', url: '/admin/filters?page=1&size=10&active=1&sort=name,asc' },
    'user-filters': { text: 'userFilters.title', url: '/admin/user-filters?page=1&size=10&active=1&sort=name,asc' },
    'organization-filters': {
        text: 'organizationFilters.title',
        url: '/admin/organization-filters?page=1&size=10&active=1&sort=name,asc'
    },
    people: { text: 'People', url: '/admin/people?tabs=users&page=1&size=10&active=1' },
    'user-fields': { text: 'User Fields', url: '/admin/user-fields?page=1&size=10&active=1&sort=name,asc' },
    'organization-fields': {
        text: 'Organization Fields',
        url: '/admin/organization-fields?page=1&size=10&active=1&sort=name,asc'
    },
    'ticket-fields': { text: 'Ticket Fields', url: '/admin/ticket-fields?page=1&size=10&active=1&sort=name,asc' },
    'ticket-forms': { text: 'Ticket Forms', url: '/admin/ticket-forms?page=1&size=10&active=1&sort=name,asc' },
    tags: { text: 'Tags', url: '/admin/tags' },
    macros: { text: 'Macros', url: '/admin/macros' },
    'advanced-reports': {
        text: 'Advanced Reports',
        url: '/admin/advanced-reports?page=1&size=10&active=1&sort=name,asc'
    }
} as const;

export const adminSidebarSettingsOptions = {
    'company-settings': { text: 'Company', url: '/admin/company-settings' },
    subscription: { text: 'Subscription', url: '/admin/subscription' },
    apps: { text: 'Apps', url: '/admin/apps' },
    'security-settings': { text: 'Security', url: '/admin/security-settings' },
    // external: { text: 'External', url: '/admin/external' },
    'schedule-settings': { text: 'Schedule', url: '/admin/schedule-settings' }
} as const;

export const adminSidebarBusinessRulesOptions = {
    automations: { text: 'Automations.title', url: '/admin/automations?page=1&size=10&active=1&sort=name,asc' },
    triggers: { text: 'Triggers', url: '/admin/triggers?page=1&size=10&active=1&sort=name,asc' },
    'service-level-agreements': { text: 'Service Level Agreements', url: '/admin/service-level-agreements' }
} as const;

export type adminSidebarBusinessRulesOptionsType = keyof typeof adminSidebarBusinessRulesOptions;

export const adminSidebarChannelOptions = {
    email: { text: 'Email', url: '/admin/email' },
    'g-social': { text: 'GSocial', url: '/admin/g-social' },
    chat: { text: 'Chat', url: '/admin/chat' },
    call: { text: 'Call', url: '/admin/call' }
} as const;

export type adminSidebarChannelOptionsType = keyof typeof adminSidebarChannelOptions;
export const adminSidebarInvisibleOptions = {
    import: null
} as const;

export type adminSidebarInvisibleOptionsType = keyof typeof adminSidebarInvisibleOptions;
export const allAdminSidebarOptions = {
    ...adminSidebarHomeOptions,
    ...adminSidebarManageOptions,
    ...adminSidebarSettingsOptions,
    ...adminSidebarBusinessRulesOptions,
    ...adminSidebarChannelOptions,
    ...adminSidebarInvisibleOptions
} as const;

export type adminSidebarManageOptionsType = keyof typeof adminSidebarManageOptions;

export type adminSidebarOptionsType = keyof typeof allAdminSidebarOptions;

/////////////////////////////////////////

export interface Change {
    fieldDefinitionKey: TicketSystemFieldsKey;
    previousValue?: string;
    newValue: string;
}

export const browserMetadataMap = {
    userAgent: 'userAgent',
    ip: 'ip',
    location: 'location'
} as const;
export interface BrowserMetadata {
    userAgent: string;
    ip: string;
    location: string;
}
export interface Event {
    browserMetadata: BrowserMetadata;
    channel: channelType;
    comment: { commentId: number; value: string; type: string };
    formId: number;
    id: number;
    items?: Change[];
    notifications: unknown[];
    ticketKey: string;
    time: number;
    userId: number;
}

/////////////////////////////////////////

export enum Languages {
    EN = 'EN',
    TR = 'TR'
}

export const languageOptionsMap = {
    TR: { value: 'TR', label: 'Turkish - Türkçe' },
    EN: { value: 'EN', label: 'English - İngilizce' }
} as const;

export type languagesType = keyof typeof Languages;

/////////////////////////////////////////

export interface LoginFormData {
    username: string;
    password: string;
}
export interface LoginPostData extends LoginFormData {
    tenantId: string;
}

export interface RegisterFormUserData {
    email: string;
    phone: string;
    fullName: string;
    password: string;
    passwordConfirm: string;
}
export type RegisterFormData = {
    companyName: string;
    tenantId: string;
    language: languagesType;
    confirmation: boolean;
} & RegisterFormUserData;

export type RegisterPostData = {
    language: languagesType;
    user: Omit<RegisterFormUserData, 'passwordConfirm'>;
    tenantId: string;
    companyName: string;
    registrationSessionId?: string;
};

export type ForgotPasswordFormData = Pick<LoginFormData, 'username'>;
export type ForgotPasswordPostData = ForgotPasswordFormData & { tenantId: string };
export type ResetPasswordFormData = Pick<RegisterFormUserData, 'password' | 'passwordConfirm'>;
export type ResetPasswordPostData = Omit<ResetPasswordFormData, 'passwordConfirm'> & { tenantId: string };

/////////////////////////////////////////

export const customFieldOptionsMap = {
    TAG: 'TAG',
    ASSIGNEE: 'ASSIGNEE',
    USER: 'USER',
    MULTI_USER: 'MULTI_USER',
    SELECT: 'SELECT',
    NUMBER_DECIMAL: 'NUMBER_DECIMAL',
    NUMBER_INTEGER: 'NUMBER_INTEGER',
    DATE: 'DATE',
    DATE_TIME: 'DATE_TIME',
    TEXT: 'TEXT',
    MULTI_SELECT: 'MULTI_SELECT',
    TEXT_AREA: 'TEXT_AREA',
    STATUS: 'STATUS',
    FORM: 'FORM',
    // autocomplete: 'autocomplete',
    RADIO: 'RADIO',
    CHECKBOX: 'CHECKBOX'
    // switch: 'switch',
} as const;

export const ticketFilterMemberFieldsMap = {
    TICKET_MEMBER_KEY: 'key',
    TICKET_MEMBER_CHANNEL_KEY: 'channel',
    TICKET_MEMBER_CREATED_AT_KEY: 'createdAt',
    TICKET_MEMBER_UPDATED_AT_KEY: 'updatedAt',
    TICKET_MEMBER_SOLVED_AT_KEY: 'solvedAt',
    USER_MEMBER_REQUESTER_ORGANIZATION: 'organization'
} as const;
export const userFilterMemberFieldsMap = {
    USER_MEMBER_ROLE: 'role',
    USER_MEMBER_ORGANIZATION: 'organization',
    USER_MEMBER_LANGUAGE: 'language'
} as const;
export const organizationFilterMemberFieldsMap = {
    ORGANIZATION_NAME: 'om.name',
    ORGANIZATION_GROUP: 'om.group',
    ORGANIZATION_DETAILS: 'om.detail',
    ORGANIZATION_NOTES: 'om.notes',
    ORGANIZATION_EXTERNAL_ID: 'os.external_id'
} as const;

export const customFieldOptions = {
    // Utku : GRISPI-795
    // ASSIGNEE: {
    //     label: 'Assignee',
    //     info: `Provide a drop-down list with values you define. The ticket will be tagged with the selected value. Attribute available in Insights.`,
    // },
    USER: {
        label: 'User',
        info: `Choose one user in a menu of choices`
    },
    MULTI_USER: {
        label: 'Multi User',
        info: `Choose one or more users in a pre-set list`
    },
    SELECT: {
        label: 'Select',
        info: `Choose one option in a menu of choices`
    },
    MULTI_SELECT: {
        label: 'Multi-select',
        info: `Choose one or more options in a pre-set list`
    },
    TEXT: {
        label: 'Text',
        info: `Add a few words`
    },
    TAG: {
        label: 'Tags',
        info: `Tags are used throughout to add additional information to tickets and organizations and/or users, which can then be used in your ticket workflow`
    },
    DATE: {
        label: 'Date',
        info: `Select a date from a calendar`
    },
    DATE_TIME: {
        label: 'Date-time',
        info: `Select a date and time from a calendar`
    },
    NUMBER_INTEGER: {
        label: 'Integer',
        info: `Enter a number with no decimal`
    },
    NUMBER_DECIMAL: {
        label: 'Decimal',
        info: `Enter a number with a decimal`
    },
    CHECKBOX: {
        label: 'Check-Box',
        info: `Check for True or False condition`
    }

    // multiline: {
    //     label: 'Multi-line',
    //     info: `Provide a drop-down list with values you define. The ticket will be tagged with the selected value. Attribute available in Insights.`,
    // },
    // checkbox: {
    //     label: 'Check-box',
    //     info: `Provide a drop-down list with values you define. The ticket will be tagged with the selected value. Attribute available in Insights.`,
    // },

    // autocomplete: {
    //     label: 'Autocomplete',
    //     info: `Provide a drop-down list with values you define. The ticket will be tagged with the selected value. Attribute available in Insights.`,
    // },
    // RADIO: {
    //     label: 'Radio',
    //     info: `Provide a drop-down list with values you define. The ticket will be tagged with the selected value. Attribute available in Insights.`,
    // },
    // switch: {
    //     label: 'Switch',
    //     info: `Provide a drop-down list with values you define. The ticket will be tagged with the selected value. Attribute available in Insights.`,
    // },
} as const;
export const customUserFieldOptions = {
    USER: {
        label: 'User',
        info: `Choose one user in a menu of choices`
    },
    MULTI_USER: {
        label: 'Multi User',
        info: `Choose one or more users in a pre-set list`
    },
    SELECT: {
        label: 'Select',
        info: `Choose one option in a menu of choices`
    },
    MULTI_SELECT: {
        label: 'Multi-select',
        info: `Choose one or more options in a pre-set list`
    },
    TEXT: {
        label: 'Text',
        info: `Add a few words`
    },
    TAG: {
        label: 'Tags',
        info: `Tags are used throughout to add additional information to tickets and organizations and/or users, which can then be used in your ticket workflow`
    },
    DATE: {
        label: 'Date',
        info: `Select a date from a calendar`
    },
    DATE_TIME: {
        label: 'Date-time',
        info: `Select a date and time from a calendar`
    },
    NUMBER_INTEGER: {
        label: 'Integer',
        info: `Enter a number with no decimal`
    },
    NUMBER_DECIMAL: {
        label: 'Decimal',
        info: `Enter a number with a decimal`
    }
} as const;
export const organizationCustomFieldOptions = {
    SELECT: {
        label: 'Select',
        info: `Choose one option in a menu of choices`
    },
    MULTI_SELECT: {
        label: 'Multi-select',
        info: `Choose one or more options in a pre-set list`
    },
    TEXT: {
        label: 'Text',
        info: `Add a few words`
    },
    TAG: {
        label: 'Tags',
        info: `Tags are used throughout to add additional information to tickets and organizations and/or users, which can then be used in your ticket workflow`
    },
    DATE: {
        label: 'Date',
        info: `Select a date from a calendar`
    },
    NUMBER_INTEGER: {
        label: 'Integer',
        info: `Enter a number with no decimal`
    },
    NUMBER_DECIMAL: {
        label: 'Decimal',
        info: `Enter a number with a decimal`
    },
    CHECKBOX: {
        label: 'Check-Box',
        info: `Check for True or False condition`
    }
} as const;

export const customFieldOptionsForEndUserMap = {
    HTML: 'HTML',
    ATTACHMENTS: 'ATTACHMENTS',
    UNTRUSTED_END_USER: 'UNTRUSTED_END_USER'
} as const;

export type customFieldOptionTypes = keyof typeof customFieldOptionsMap | keyof typeof customFieldOptionsForEndUserMap;

export const permissionsOptions = {
    AGENT_ONLY: { value: 'AGENT_ONLY', label: 'Agent Only', tooltip: 'Field can only be edited by an agent' },
    EDITABLE_BY_END_USERS: {
        value: 'EDITABLE_BY_END_USERS',
        label: 'Editable for Customers',
        tooltip: 'Field can only be edited by the Customer when submitting a request'
    },
    READONLY_FOR_END_USERS: {
        value: 'READONLY_FOR_END_USERS',
        label: 'Read-only for Customers',
        tooltip: 'Field value is visible to Customer on their request page, but cannot be edited.'
    }
} as const;

export interface ICustomSelectOption {
    key: string;
    // order?: number;
    label: string;
    value: string;
}

export interface CustomFieldOption {
    order: number;
    label: string;
    value: string | number;
    id: number;
    defaultValue?: boolean;
}
export interface ICustomFieldOption {
    defaultValue?: string;
    description?: string;
    fieldType: customFieldOptionTypes;
    label?: string;
    optionList?: ICustomSelectOption[];
    placeholder: string;
}

export const customFieldSaveOptionsMap = {
    save: 'save',
    another: 'another'
} as const;
export const customFieldSaveOptions = {
    save: 'Save',
    another: 'Save and add another'
} as const;

export type customFieldSaveOptionTypes = keyof typeof customFieldSaveOptions;

/////////////////////////////////////////

export type SetStateType<T> = Dispatch<SetStateAction<T>>;
export type customSWRConfig = SWRConfiguration & { requestFlag?: boolean };
export type customSWRResponse<T> = Partial<SWRResponse<T, Error>>;
export type customSWRPaginatedResponse<T> = Partial<SWRResponse<PaginatedData<T>, Error>> & { isLoading?: boolean };

/////////////////////////////////////////

export const settingsKey = {
    APPLY_IP_RESTRICTION_TO_CUSTOMERS: 'APPLY_IP_RESTRICTION_TO_CUSTOMERS',
    ACCEPT_WILDCARD_EMAILS: 'ACCEPT_WILDCARD_EMAILS',
    COMPANY_NAME: 'COMPANY_NAME',
    COMPANY_LOGO: 'COMPANY_LOGO',
    DEFAULT_CONVERSATION_VISIBILITY: 'DEFAULT_CONVERSATION_VISIBILITY',
    DEFAULT_USER_GROUP: 'DEFAULT_USER_GROUP',
    DEFAULT_FORM: 'DEFAULT_FORM',
    DEFAULT_EMAIL_FORM: 'DEFAULT_EMAIL_FORM',
    DEFAULT_CHAT_FORM: 'DEFAULT_CHAT_FORM',
    DEFAULT_CALL_FORM: 'DEFAULT_CALL_FORM',
    IP_WHITELIST: 'IP_WHITELIST',
    LANGUAGE: 'LANGUAGE',
    OWNER_ID: 'OWNER_ID',
    RESTRICTED_ASSIGNEE: 'RESTRICTED_ASSIGNEE',
    SEND_AGENT_EMAILS: 'SEND_AGENT_EMAILS',
    SEND_CUSTOMER_EMAILS: 'SEND_CUSTOMER_EMAILS',
    SEND_SURVEY_EMAILS: 'SEND_SURVEY_EMAILS',
    SCHEDULED_JOB_TICKET_CLOSE_AFTER_DAYS: 'SCHEDULED_JOB_TICKET_CLOSE_AFTER_DAYS',
    SCHEDULED_JOB_SEND_NOTIFICATION_MAIL: 'SCHEDULED_JOB_SEND_NOTIFICATION_MAIL',
    SIGNATURE: 'SIGNATURE',
    TIMEZONE: 'TIMEZONE',
    SHOW_QUICK_FLOWS: 'SHOW_QUICK_FLOWS',
    PUBLIC_REPLY_ELIGIBLE_GROUP: 'PUBLIC_REPLY_ELIGIBLE_GROUP',
    DEFAULT_TICKET_AUTHORIZATION: 'DEFAULT_TICKET_AUTHORIZATION',
    PLAY_SESSION_SKIP_REQUIREMENT: 'PLAY_SESSION_SKIP_REQUIREMENT'
} as const;

export type companySettingsKeyType = keyof typeof settingsKey;
export type securitycompanySettingsKeyType = 'IP_WHITELIST' | 'APPLY_IP_RESTRICTION_TO_CUSTOMERS';

export interface ISingleCompanySetting<T extends companySettingsKeyType> {
    id: number;
    key: T;
    value: string;
}
export interface ICompanySettings {
    id: number;
    key: companySettingsKeyType;
    value: string;
}

export type companySettingsPostData = Partial<{
    [K in companySettingsKeyType]: K extends 'LANGUAGE'
        ? languagesType
        : K extends
                | 'APPLY_IP_RESTRICTION_TO_CUSTOMERS'
                | 'ACCEPT_WILDCARD_EMAILS'
                | 'SEND_AGENT_EMAILS'
                | 'SEND_CUSTOMER_EMAILS'
          ? 'true' | 'false'
          : K extends 'COMPANY_LOGO'
            ? File
            : string;
}>;

/////////////////////////////////////////

export const agentStatusOptionsMap = {
    1: 'Offline',
    2: 'All Calls',
    3: 'Inbound Calls',
    4: 'Outbound Calls',
    5: 'Call Logging',
    6: 'Back Office',
    7: 'Break',
    8: 'Lunch Break',
    9: 'Chat',
    10: 'All Calls + Chat',
    11: 'Inbound Calls + Chat',
    12: 'Outbound Calls + Chat',
    13: 'Call Logging + Chat'
} as const;
export type agentStatusIdType = keyof typeof agentStatusOptionsMap;
export type agentStatusLabelType = (typeof agentStatusOptionsMap)[agentStatusIdType];

export const systemStatusOptionsByAgentStatusIdMap = {
    1: 'OFFLINE',
    2: 'ONLINE',
    3: 'ONLINE',
    4: 'ONLINE',
    5: 'ONLINE',
    6: 'ONLINE',
    7: 'AWAY',
    8: 'AWAY',
    9: 'ONLINE',
    10: 'ONLINE',
    11: 'ONLINE',
    12: 'ONLINE',
    13: 'ONLINE'
} as const;
export type systemStatusType = (typeof systemStatusOptionsByAgentStatusIdMap)[agentStatusIdType];

// callPluginStatus types
export enum callPluginStatusMap {
    OFFLINE = 'OFFLINE',
    ONLINE = 'ONLINE',
    ONLINE_INBOUND = 'ONLINE_INBOUND',
    ONLINE_OUTBOUND = 'ONLINE_OUTBOUND',
    AFTER_CALL_OPS = 'AFTER_CALL_OPS',
    AWAY = 'AWAY'
}
export const callPluginStatusOptionsByAgentStatusIdMap = {
    1: 'OFFLINE',
    2: 'ONLINE',
    3: 'ONLINE_INBOUND',
    4: 'ONLINE_OUTBOUND',
    5: 'AFTER_CALL_OPS',
    6: 'OFFLINE',
    7: 'AWAY',
    8: 'AWAY',
    9: 'OFFLINE',
    10: 'ONLINE',
    11: 'ONLINE_INBOUND',
    12: 'ONLINE_OUTBOUND',
    13: 'AFTER_CALL_OPS'
} as const;
export type callPluginStatusType = (typeof callPluginStatusOptionsByAgentStatusIdMap)[agentStatusIdType];

// FIXME: destan @resting is not implemented currently
// FIXME: destan ACW (in call-app) is coming to GRISPI-UI as 'away'

export const callClientStatusToCallPluginStatusMap = {
    offline: 'OFFLINE',
    online: 'ONLINE',
    away: 'AWAY',
    inboundOnline: 'ONLINE_INBOUND',
    outboundOnline: 'ONLINE_OUTBOUND',
    resting: 'AFTER_CALL_OPS'
} as const;
export const callPluginStatusToAgentStatusIdMap = {
    OFFLINE: 6, // agentStatusOptionsMap[6] = 'Back Office'
    ONLINE: 2, // agentStatusOptionsMap[2] = 'All Calls'
    AWAY: 7, // agentStatusOptionsMap[7] = 'Break'
    ONLINE_INBOUND: 3, // agentStatusOptionsMap[3] = 'Inbound Calls'
    ONLINE_OUTBOUND: 4, // agentStatusOptionsMap[4] = 'Outbound Calls'
    AFTER_CALL_OPS: 5 // agentStatusOptionsMap[5] = 'Call Logging'
} as const;
export const callPluginStatusToCallClientStatusMap = {
    OFFLINE: 'offline',
    ONLINE: 'online',
    AWAY: 'away',
    ONLINE_INBOUND: 'inboundOnline',
    ONLINE_OUTBOUND: 'outboundOnline',
    AFTER_CALL_OPS: 'resting'
} as const;

// chatPluginStatus types
export const chatPluginStatusOptionsByAgentStatusIdMap = {
    1: 'OFFLINE',
    2: 'OFFLINE',
    3: 'OFFLINE',
    4: 'OFFLINE',
    5: 'OFFLINE',
    6: 'OFFLINE',
    7: 'AWAY',
    8: 'AWAY',
    9: 'ONLINE',
    10: 'ONLINE',
    11: 'ONLINE',
    12: 'ONLINE',
    13: 'ONLINE'
} as const;
export type chatPluginStatusType = (typeof chatPluginStatusOptionsByAgentStatusIdMap)[agentStatusIdType];

export interface AgentStatus {
    call: callPluginStatusType;
    chat: chatPluginStatusType;
    i18n: string;
    id: agentStatusIdType;
    label: agentStatusLabelType;
    listOrder: number;
    system: systemStatusType;
    userDefined: boolean;
}

export interface CurrentAgentStatus {
    activatedAt: number;
    status: AgentStatus;
}

/////////////////////////////////////////

export enum AuxGrispiFormItemsName {
    channel = 'channel',
    comment = 'comment',
    commentType = 'commentType',
    distributionListTo = 'distributionListTo',
    activeForm = 'activeForm'
}
export enum FilterConditionAssigneeFieldKey {
    user = 'ts.assignee_user',
    group = 'ts.assignee_group'
}

export enum TicketSystemFieldsKey {
    assignee = 'ts.assignee',
    creator = 'ts.creator',
    emailCc = 'ts.email_ccs',
    externalId = 'ts.external_id',
    followers = 'ts.followers',
    formId = 'ts.form',
    priority = 'ts.priority',
    requester = 'ts.requester',
    status = 'ts.status',
    subject = 'ts.subject',
    tags = 'ts.tags',
    type = 'ts.type',

    // gSocial specific
    gSocial = 'ts.gsocial_account_id',

    // end-user help form specific
    description = 'ts.description',
    attachments = 'ts.attachments',
    untrusted_end_user = 'ts.untrusted_end_user',

    // deal form specific
    stage = 'dd.stage',
    result = 'dd.result',
    lossReasons = 'dd.loss_reasons',
    unqualifiedReasons = 'dd.unqualified_reasons',
    source = 'dd.source',
    valueAmount = 'dd.value_amount',
    winLikelihoodProbability = 'dd.win_likelihood_probability',
    expectedCloseDate = 'dd.expected_close_date',
    lastCall = 'dd.last_call',
    nextCall = 'dd.next_call',
    addedOn = 'dd.added_on'
}
export const TicketSystemFieldNamesMap = [
    'Assignee',
    'Attachments (Customer form)',
    'Creator',
    'Description (Customer form)',
    'Email CCs',
    'Ticket External ID',
    'Followers',
    'Form',
    'Priority',
    'Requester',
    'Status',
    'Subject',
    'Tags',
    'Type',
    'Customer credentials (Customer form)',
    'Win Likelihood/Probability (%)',
    'Value/Amount',
    'Unqualified Reasons',
    'Stage',
    'Result',
    'Next Call',
    'Loss Reasons',
    'Last Call',
    'Expected Close Date',
    'Deal Source',
    'Added On'
];
export const BaseTicketSystemFieldsKeyMap = {
    assignee: 'ts$assignee',
    creator: 'ts$creator',
    emailCc: 'ts$email_ccs',
    followers: 'ts$followers',
    formId: 'ts$form',
    priority: 'ts$priority',
    requester: 'ts$requester',
    status: 'ts$status',
    subject: 'ts$subject',
    tags: 'ts$tags',
    type: 'ts$type'
} as const;
export type BaseTicketSystemFieldsKeyType =
    (typeof BaseTicketSystemFieldsKeyMap)[keyof typeof BaseTicketSystemFieldsKeyMap];

export const EndUserFormSystemFieldsKeyMap = {
    attachments: 'ts$attachments',
    description: 'ts$description',
    untrusted_end_user: 'ts$untrusted_end_user'
} as const;
export type EndUserFormSystemFieldsKeyType =
    (typeof EndUserFormSystemFieldsKeyMap)[keyof typeof EndUserFormSystemFieldsKeyMap];

export const TicketSystemFieldsKeyMap = {
    ...BaseTicketSystemFieldsKeyMap,
    ...EndUserFormSystemFieldsKeyMap
} as const;
export const TICKET_FORM_FIELDS_KEY = 'fields';
export const USER_FORM_FIELDS_KEY = 'fields';
export const ORGANIZATION_FORM_FIELDS_KEY = 'fields';
export type CustomFieldsKeyType = `${string | 'ticket$custom'}.${string}`;
export type TicketSystemFieldsKeyType = BaseTicketSystemFieldsKeyType | EndUserFormSystemFieldsKeyType;
export type TicketFieldsKeyType = TicketSystemFieldsKeyType | CustomFieldsKeyType;

export enum TableDataSourceKeyMap {
    FILTER = 'FILTER',
    USER_FILTER = 'USER_FILTER',
    ORGANIZATION_FILTER = 'ORGANIZATION_FILTER',
    TRIGGER = 'TRIGGER',
    ADVANCED_REPORT = 'ADVANCED_REPORT',
    AUTOMATION = 'AUTOMATION'
}

export interface ChatSettings {
    urls: string[];
    group: number;
    concurrencyLimit: number;
}

export type CallSettings = {
    group: number;
    showCallRecords: defaultCallRecordsVisibilityOptionsMap;
    activeCallPlugin: string;
};
export type CallPluginSettings = {
    settingsJson: string;
};
export interface UserSignature {
    key: string;
    value: string;
}

export enum TicketAuthorizationTypeMap {
    ASSIGNED_USER_TICKETS = 'ASSIGNED_USER_TICKETS',
    ASSIGNED_GROUP_TICKETS = 'ASSIGNED_GROUP_TICKETS',
    ORGANIZATION_TICKETS = 'ORGANIZATION_TICKETS',
    ALL_TICKETS = 'ALL_TICKETS'
}

export interface UserTicketAuthorization {
    key: string;
    value: string;
}

export type TicketPlaySession = {
    playSessionFilterName: string;
    playSessionFilterId: string;
    playedTicketKeys: string[];
    currentIndex: number;
    currentTicketKey: string;
};

export enum skipTicketPlayTypeMap {
    'SKIP_WITH_NO_REASON' = 'SKIP_WITH_NO_REASON',
    'SKIP_WITH_REQUIRED_REASON' = 'SKIP_WITH_REQUIRED_REASON',
    'SKIP_WITH_NO_REQUIRED_REASON' = 'SKIP_WITH_NO_REQUIRED_REASON'
}
export type skipTicketPlayType = keyof typeof skipTicketPlayTypeMap;
